/**
 * Pure seams of the client chat screen (client-chat-screen D4, D7), shared with the website's
 * messages (shared-client-screens D4 "Chat"). No React, no fetching: merge order, the ledger
 * refresh, day groups, the footer door's copy, the media line, a notice's state line, a
 * message's delivery, the filter labels and what an empty view says.
 */
import type { AutomaticMessageState, ClientDoor, ClientThreadEntry, ClientThreadResponse } from '@telify/shared';
import { dateKey, previousDateKey } from '@/lib/chat/dayKeys';

export type ClientChatMessageEntry = Extract<ClientThreadEntry, { kind: 'chat-message' }>;

/** Chat-source entries carry a sequence number of the conversation. */
const isChat = (entry: ClientThreadEntry) => entry.kind === 'chat-message' || entry.kind === 'chat-media';

/** The composer's temporary bubble: its id is its idempotency key until the server answers. */
export const isOptimistic = (entry: ClientThreadEntry): entry is ClientChatMessageEntry =>
  entry.kind === 'chat-message' && entry.idempotencyKey !== undefined && entry.id === entry.idempotencyKey;

/** Display order, as the server sorts (time, then ledger before chat, then `seq` or id). */
export function compareClientEntries(a: ClientThreadEntry, b: ClientThreadEntry): number {
  const byTime = Date.parse(a.at) - Date.parse(b.at);
  if (byTime !== 0) return byTime;
  const aChat = isChat(a);
  const bChat = isChat(b);
  if (aChat !== bChat) return aChat ? 1 : -1;
  if (a.kind !== 'automatic' && b.kind !== 'automatic' && aChat && bChat && a.seq !== b.seq) return a.seq - b.seq;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Inserts or replaces entries by id and returns a new list in display order. A stored
 * secure message carries its idempotency key: it replaces the composer's placeholder
 * under that key, and a placeholder never replaces the stored row.
 */
export function mergeClientEntries(
  current: readonly ClientThreadEntry[],
  incoming: readonly ClientThreadEntry[],
): ClientThreadEntry[] {
  if (incoming.length === 0) return current as ClientThreadEntry[];
  const byId = new Map(current.map((entry) => [entry.id, entry]));
  const idByKey = new Map<string, string>();
  for (const entry of current) {
    if (entry.kind === 'chat-message' && entry.idempotencyKey) idByKey.set(entry.idempotencyKey, entry.id);
  }
  for (const entry of incoming) {
    const key = entry.kind === 'chat-message' ? entry.idempotencyKey : undefined;
    if (key) {
      const existingId = idByKey.get(key);
      const existing = existingId === undefined ? undefined : byId.get(existingId);
      if (existing && existingId !== undefined) {
        if (isOptimistic(entry) && !isOptimistic(existing)) continue; // the stored row stays
        byId.delete(existingId);
      }
      idByKey.set(key, entry.id);
    }
    byId.set(entry.id, entry);
  }
  return [...byId.values()].sort(compareClientEntries);
}

/**
 * Applies a History response's ledger refresh: removals by id, upserts replaced by id. An
 * upsert older than the loaded range is left to paging, which reads it fresh; with no older
 * history (`hasOlder` false) every upsert applies.
 */
export function applyClientLedgerRefresh(
  current: readonly ClientThreadEntry[],
  ledger: ClientThreadResponse['ledger'],
  hasOlder: boolean,
): ClientThreadEntry[] {
  if (!ledger) return current as ClientThreadEntry[];
  const removed = new Set(ledger.removals);
  const kept = removed.size > 0 ? current.filter((entry) => !removed.has(entry.id)) : (current as ClientThreadEntry[]);
  const oldest = kept.length > 0 ? Date.parse(kept[0].at) : Number.NEGATIVE_INFINITY;
  const loadedIds = new Set(kept.map((entry) => entry.id));
  const upserts = ledger.upserts.filter(
    (entry) => !hasOlder || loadedIds.has(entry.id) || Date.parse(entry.at) >= oldest,
  );
  return mergeClientEntries(kept, upserts);
}

/** True when `incoming` holds a business message the screen did not already show (the read mark's trigger). */
export function hasNewBusinessMessage(current: readonly ClientThreadEntry[], incoming: readonly ClientThreadEntry[]): boolean {
  const known = new Set(current.map((entry) => entry.id));
  return incoming.some((entry) => entry.direction === 'outbound' && entry.kind !== 'automatic' && !known.has(entry.id));
}

/** Day divider copy (frame 1) in the device's timezone: Today, Yesterday (the calendar date before
 * today, D6), "Tue, May 12", with the year when it differs. */
export function clientDayLabel(iso: string, timezone: string, now: Date = new Date()): string {
  const key = dateKey(iso, timezone);
  const today = dateKey(now, timezone);
  if (key === today) return 'Today';
  if (key === previousDateKey(today)) return 'Yesterday';
  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' as const }),
    timeZone: timezone,
  }).format(new Date(iso));
}

export interface ClientThreadDay {
  key: string;
  label: string;
  entries: ClientThreadEntry[];
}

/** Splits display-ordered items into days in the client's own timezone; `atOf` reads an item's time. */
export function groupByClientDay<T>(
  items: readonly T[],
  atOf: (item: T) => string,
  timezone: string,
  now: Date = new Date(),
): Array<{ key: string; label: string; items: T[] }> {
  const days: Array<{ key: string; label: string; items: T[] }> = [];
  for (const item of items) {
    const at = atOf(item);
    const key = dateKey(at, timezone);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(item);
    else days.push({ key, label: clientDayLabel(at, timezone, now), items: [item] });
  }
  return days;
}

/** Splits display-ordered entries into days in the client's own timezone. */
export function groupClientDays(entries: readonly ClientThreadEntry[], timezone: string, now: Date = new Date()): ClientThreadDay[] {
  return groupByClientDay(entries, (entry) => entry.at, timezone, now).map(({ key, label, items }) => ({ key, label, entries: items }));
}

export function formatClientTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone }).format(new Date(iso));
}

/** The footer door's copy (frame 4): Open booking, Fill it out, Open invite, Open offering. */
export function clientDoorLabel(door: ClientDoor): string {
  switch (door.kind) {
    case 'form':
      return 'Fill it out';
    case 'invite':
      return 'Open invite';
    case 'offering':
      return 'Open offering';
    case 'booking':
      // Every booking door reads what it opens, money notices included (Gabriel 2026-10-07).
      return 'Open booking';
  }
}

const MEDIA_NOUNS: Record<string, string> = {
  photo: 'a photo',
  voice: 'a voice note',
  video: 'a video',
  document: 'a file',
  sticker: 'a sticker',
  audio: 'audio',
  video_note: 'a video message',
  animation: 'a GIF',
  contact: 'a contact',
  location: 'a location',
  venue: 'a location',
  poll: 'a poll',
};

/** Media the client sent in Telegram, as a quiet line (frame 4 notes): "You sent a photo". */
export function clientMediaLine(mediaType: string): string {
  return `You sent ${MEDIA_NOUNS[mediaType] ?? 'a message'}`;
}

const STATE_WORDS: Record<AutomaticMessageState['status'], string> = {
  awaiting: 'Waiting for you',
  submitted: 'Submitted',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
  booked: 'Booked',
  pending: 'Waiting for you',
  passed: 'Passed',
};

/** A form request's or an invite's state (frame 4): Waiting for you · Submitted May 12 · Expired · Withdrawn. */
export function clientStateLine(state: AutomaticMessageState, timezone: string): string {
  const submittedOn = state.kind === 'form-request' && state.status === 'submitted' && state.submittedAt
    ? ` ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: timezone }).format(new Date(state.submittedAt))}`
    : '';
  return `${STATE_WORDS[state.status]}${submittedOn}`;
}

/** Why a send did not land: unsent can be tried again, refused cannot. */
export type ClientSendIssue = 'unsent' | 'refused';

/**
 * A secure message's delivery, the client's own only: "Sending…" until stored, a tick once
 * stored (a tick means stored, nothing more), or the issue that stopped it. Null for anything
 * else: the studio's messages and the Telegram room carry no delivery state.
 */
export function clientDelivery(
  entry: ClientThreadEntry,
  secure: boolean,
  issue: ClientSendIssue | null,
): 'sending' | 'stored' | ClientSendIssue | null {
  if (entry.kind !== 'chat-message' || entry.direction !== 'inbound' || !secure) return null;
  if (issue) return issue;
  return isOptimistic(entry) ? 'sending' : 'stored';
}

/** Every filter's label, the app's History lenses and the website's filter row alike. */
export const CHAT_FILTER_LABELS = {
  all: 'All',
  secure: 'Secure',
  telegram: 'Telegram',
  chat: 'Chat',
  bookings: 'Bookings',
  forms: 'Forms',
  payments: 'Payments',
} as const;

/** A view of the client's messages: the Secure room, the Telegram room whole, a lens, or All (both rooms, the website's). */
export type ChatView = keyof typeof CHAT_FILTER_LABELS;

/**
 * What an empty view says (rows M8, M9): the Secure note only where Secure alone shows, the
 * Telegram chat by name, a lens by its label, and All, which holds both rooms, "No messages yet."
 * (Gabriel 2026-10-23).
 */
export function chatEmptyState(view: ChatView): { kind: 'explainer' } | { kind: 'line'; text: string } {
  switch (view) {
    case 'secure':
      return { kind: 'explainer' };
    case 'telegram':
      return { kind: 'line', text: 'Nothing from your Telegram chat yet.' };
    case 'all':
      return { kind: 'line', text: 'No messages yet.' };
    default:
      return { kind: 'line', text: `Nothing under ${CHAT_FILTER_LABELS[view]} yet.` };
  }
}

/** What the messages say when the client has no chat with the studio (row M10): the app's words on every surface. */
export const CHAT_NOT_FOUND_LINE = 'Chat isn’t available right now.';

/** The client's own browser timezone (CLAUDE.md: clients use the browser tz). */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
